import React from 'react';
import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend } from 'recharts';
import { Category } from '@/types/assessment';

interface ChartDataItem {
  name: string;
  current: number;
  target: number;
}

interface RenderOptions {
  categories: Category[];
  scoresByCategory: Record<string, { current: number; target: number }>;
}

/**
 * Renders the assessment results radar chart to a PNG data URL.
 * This ensures the PDF chart is identical to the browser display.
 */
export async function renderAssessmentResultsRadarChartPng(
  options: RenderOptions
): Promise<string | null> {
  const { categories, scoresByCategory } = options;
  
  // Prepare chart data
  const chartData: ChartDataItem[] = categories.map(category => {
    const scores = scoresByCategory[category.id] || { current: 0, target: 0 };
    return {
      name: category.name,
      current: scores.current,
      target: scores.target,
    };
  });

  // Create a container for rendering
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  container.style.width = '600px';
  container.style.height = '500px';
  container.style.backgroundColor = 'white';
  document.body.appendChild(container);

  // Chart component using JSX
  const ChartComponent = () => (
    <div 
      style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: 'white'
      }}
    >
      <RadarChart
        cx="50%"
        cy="54%"
        outerRadius="60%"
        width={560}
        height={440}
        data={chartData}
        margin={{ top: 60, right: 80, bottom: 40, left: 80 }}
      >
        <PolarGrid gridType="circle" stroke="#e0e0e0" />
        <PolarAngleAxis
          dataKey="name"
          tick={{ fill: '#403E43', fontSize: 12, fontWeight: 500, dy: -8 }}
          axisLine={{ strokeWidth: 2 }}
          tickLine={false}
        />
        <PolarRadiusAxis
          angle={90}
          domain={[0, 4]}
          tickCount={5}
          tick={{ fill: '#8A898C' }}
          stroke="#e0e0e0"
          axisLine={false}
        />
        {/* Current score - gray with 30% opacity */}
        <Radar
          name="Current"
          dataKey="current"
          stroke="#8E9196"
          fill="#8E9196"
          fillOpacity={0.3}
          dot={true}
          isAnimationActive={false}
        />
        {/* Target score - orange with 50% opacity */}
        <Radar
          name="Target"
          dataKey="target"
          stroke="#e87a2e"
          fill="#e87a2e"
          fillOpacity={0.5}
          dot={true}
          isAnimationActive={false}
        />
        <Legend
          verticalAlign="bottom"
          wrapperStyle={{ paddingTop: '10px' }}
        />
      </RadarChart>
    </div>
  );

  try {
    // Render using createRoot
    const root = ReactDOM.createRoot(container);
    root.render(<ChartComponent />);

    // Wait for the chart to render
    await new Promise(resolve => setTimeout(resolve, 500));

    // Capture the chart as an image
    const canvas = await html2canvas(container, {
      backgroundColor: 'white',
      scale: 2, // Higher resolution
      logging: false,
      useCORS: true
    });

    // Clean up
    root.unmount();
    document.body.removeChild(container);

    // Return the data URL
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Error rendering radar chart to image:', error);
    // Clean up on error
    if (container.parentNode) {
      document.body.removeChild(container);
    }
    return null;
  }
}
